import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Actions from '../Actions/Actions.js';
import Grid from '../Grid/Grid.js';
import Pill from '../Pill/Pill.js';
import Tooltip from '../Tooltip/Tooltip.js';
import Translate from '../Translate/Translate.js';

import { IAccordionPanelConfiguration, IAccordionPanelProps, IAccordionPanelState } from './models/index.js';
import Content from './styledComponents/Content.js';
import Header from './styledComponents/Header.js';
import HeaderText from './styledComponents/HeaderText.js';
import Toggle from './styledComponents/Toggle.js';
import Wrapper from './styledComponents/Wrapper.js';
import { ACCORDION_PANEL_CATEGORY_KEY, ACCORDION_PANEL_COMPONENT_KEY } from './AccordionPanel.meta.js';

export class AccordionPanel extends ComponentBase<
	IAccordionPanelProps,
	IAccordionPanelState,
	IAccordionPanelConfiguration
> {
	categoryKey = ACCORDION_PANEL_CATEGORY_KEY;
	componentKey = ACCORDION_PANEL_COMPONENT_KEY;

	isOpen: boolean;

	constructor(props: IAccordionPanelProps) {
		super(props);
		this.isOpen = props.isOpen || false;
		makeObservable(this, {
			isOpen: observable,
			setIsOpen: action,
			toggleOpen: action,
			toggle: computed,
			toggleIcon: computed,
			content: computed,
		});
	}

	setIsOpen(isOpen: boolean) {
		this.isOpen = isOpen;
	}

	toggleOpen = () => {
		const { onTogglePanel, panelKey } = this.props;
		this.isOpen = !this.isOpen;
		if (onTogglePanel) {
			onTogglePanel(panelKey, this.isOpen);
		}
	};

	componentDidUpdate(previousProps: IAccordionPanelProps) {
		const { isOpen } = this.props;
		if (isOpen !== undefined && previousProps.isOpen !== isOpen) {
			this.setIsOpen(isOpen);
		}
	}

	get toggle() {
		const { toggle } = this.props;
		const defaultToggle = {
			show: true,
			align: 'right',
		};
		return { ...defaultToggle, ...toggle };
	}

	get toggleIcon() {
		const { align } = this.toggle;
		const asset = this.isOpen ? 'base.accordionPanel.collapseIcon' : 'base.accordionPanel.expandIcon';
		return <Toggle data-align={align} asset={asset} />;
	}

	get content() {
		const { content, grid, children } = this.props;
		if (grid) {
			return <Grid {...grid} />;
		}
		return content || children;
	}

	render() {
		const { className, heading, headingTooltip, headingPill, headingActions } = this.props;
		const isOpen = this.isOpen;
		const togglePosition = (this.toggle.show && this.toggle.align) || 'none';
		const actionsPosition = (headingActions && headingActions.align) || 'right';
		const pillPosition = (headingPill && headingPill.align) || 'right';

		return (
			<Wrapper className={className}>
				<Header data-is-open={isOpen} onClick={this.toggleOpen}>
					{togglePosition === 'left' && this.toggleIcon}
					{headingActions && actionsPosition === 'left' && <Actions {...headingActions} />}
					<HeaderText>
						{headingPill && pillPosition === 'left' && <Pill {...headingPill} />}
						<Translate>{heading}</Translate>
						{headingTooltip && <Tooltip {...headingTooltip} />}
						{headingPill && pillPosition === 'right' && <Pill {...headingPill} />}
					</HeaderText>
					{headingActions && actionsPosition === 'right' && <Actions {...headingActions} />}
					{togglePosition === 'right' && this.toggleIcon}
				</Header>
				{isOpen && <Content>{this.content}</Content>}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(AccordionPanel),
	ACCORDION_PANEL_CATEGORY_KEY,
	ACCORDION_PANEL_COMPONENT_KEY,
);
